/**
 * Hook Read ("Play RB") as a responsibility spec — the inside linebacker / hook defender.
 *
 * He keys the back but is really responsible for an AREA. The read, in his own terms:
 *
 *   - The back releases centrally and settles (a checkdown) → take him man-to-man.
 *   - The back is fast to the flat, releases to someone else's side, or stays in → he is not
 *     mine. Hold the hook and look for crossers.
 *   - A receiver crosses into my hook area → man him.
 *   - Nobody shows → keep the slow drop and hold the zone.
 *
 * Unlike the Palms and apex migrations, this one is behavior-preserving. The hand-written
 * policy already used its RB classification as a TIMELINE (`i >= rb.commitIdx`) rather than
 * acting on it from the snap, so it was never clairvoyant — it just expressed the same read
 * imperatively.
 *
 * This is the spec that motivated `bindings`. "Man the first crosser into my zone" needs the
 * defender to remember WHICH receiver he picked up, and then keep trailing that specific one;
 * a boolean latch cannot express it.
 */

import { HookReadParams } from '../../types';
import { KeyObservables, isSettled, isComingBackToLos, isHardBreak, isBreakingToward, projectedPosition } from '../observables';
import { ReadContext, ReadAction, ResponsibilitySpec } from '../responsibility';

/** The back got wide in a hurry while still shallow — a flat release, not a checkdown. */
const rbWentToFlat = (rb: KeyObservables, p: HookReadParams) =>
  rb.lateralFromStart > p.flatThresholdX && rb.position.y > p.flatDepthY;

/** The back released between the tackles and settled — the classic checkdown. */
const rbSettledCentral = (rb: KeyObservables, p: HookReadParams) =>
  rb.distanceFromStart > p.releaseThresholdDist &&
  rb.lateralFromStart <= p.flatThresholdX &&
  (isComingBackToLos(rb, p.backToLosSpeed) || isSettled(rb, p.settleSpeedFraction));

/**
 * Is the back releasing to THIS defender's side?
 *
 * Only meaningful when `ownSideSign` is set — Cover 3 splits the middle between two hook
 * defenders, and a back releasing away is simply not this one's problem. Undefined means he is
 * the only hook defender, so every release is his.
 */
const rbOnMySide = (rb: KeyObservables, p: HookReadParams) =>
  p.ownSideSign === undefined || Math.sign(rb.position.x - p.crosserMiddleX) === p.ownSideSign;

/** Whether this defender will actually man the back, given what he has seen so far. */
const willManRb = (ctx: ReadContext<HookReadParams>) =>
  ctx.latches.rbCheckdownMine || (!!ctx.params.manAnyReleaseToSide && ctx.latches.rbFlatMine);

/** A receiver working across the field into this defender's hook area. */
const isCrosserInMyZone = (obs: KeyObservables, p: HookReadParams) => {
  const inBand = obs.position.y >= p.crosserBandMinY && obs.position.y <= p.crosserBandMaxY;
  const crossing = Math.abs(obs.velocity.x) > p.crosserLateralSpeed;
  const towardMiddle = Math.sign(obs.velocity.x) === Math.sign(p.crosserMiddleX - obs.position.x);
  const inZone = Math.abs(obs.position.x - p.hookLandmark.x) <= p.crosserZoneHalfWidth;
  return inBand && crossing && towardMiddle && inZone;
};

/**
 * How far ahead of a crossing receiver the defender aims. A crosser runs at essentially the
 * defender's own top speed, so chasing his current spot never closes the gap — it only shrinks
 * when the receiver stops. Taking an angle at where he is headed is what a hook defender
 * actually does, and it is legitimate: pure extrapolation of observed velocity.
 */
const PURSUIT_LEAD_SECONDS = 0.45;

/** Trail a receiver from just underneath, cutting off where he is headed rather than chasing. */
const trail = (obs: KeyObservables, p: HookReadParams, lead = 0): ReadAction => {
  const aim = lead > 0 ? projectedPosition(obs, lead) : obs.position;
  return { target: { x: aim.x, y: aim.y + p.trailCushion }, speed: p.maxSpeed };
};

/**
 * Downfield speed (units/sec) above which #2 counts as running away from an underneath defender
 * rather than being available to him. A settling curl sits near zero; a vertical is around -30.
 */
const PUSH_VERTICAL_ESCAPE_SPEED = 12;

/** #2, if this defender has him available to push to. */
const key2Of = (ctx: ReadContext<HookReadParams>): KeyObservables | undefined =>
  ctx.params.pushKey2Slot === undefined ? undefined : ctx.partners[ctx.params.pushKey2Slot];

/**
 * The "Push" call: the back got out to the flat where this defender cannot reach him, so he
 * pushes coverage of the back to the apex and takes #2 himself.
 *
 * Conditional on reachability, per the rule docs — a hook who can reach neither the back nor #2
 * does NOT push, he keeps defending his zone. And in Palms, if #2 broke out shallow the corner
 * has already made the "Out" call and owns him, so there is nobody to push onto.
 *
 * All of it is derived from what this defender can see. He is never told the corner made a call;
 * he watches the same #2 release and draws the same conclusion.
 */
const canPushToKey2 = (ctx: ReadContext<HookReadParams>): boolean => {
  const key2 = key2Of(ctx);
  if (!key2 || ctx.params.pushReachX === undefined) return false;

  // The corner has him on an "Out" call — nothing to take.
  if (
    ctx.params.pushSuppressOutSignX !== undefined &&
    isHardBreak(key2, 40) &&
    isBreakingToward(key2, ctx.params.pushSuppressOutSignX, ctx.params.crosserLateralSpeed) &&
    key2.position.y > ctx.params.crosserBandMinY
  ) {
    return false;
  }

  // A receiver climbing vertically is NOT reachable, even while he happens to be passing through
  // the matchable depth band on his way deep. The corner and safety have him; an underneath
  // defender who chased would be running under a route already gone. Without this the hook
  // pushes onto a straight vertical for the tick or two he transits the band.
  const runningPastMe = key2.velocity.y < -PUSH_VERTICAL_ESCAPE_SPEED;

  const matchableDepth =
    key2.position.y >= ctx.params.crosserBandMinY && key2.position.y <= ctx.params.crosserBandMaxY;
  const withinReach = Math.abs(key2.position.x - ctx.defender.x) <= ctx.params.pushReachX;
  return matchableDepth && withinReach && !runningPastMe;
};

export const HOOK_READ_SPEC: ResponsibilitySpec<HookReadParams> = {
  id: 'hook',
  initialTechnique: 'backpedal',

  latches: {
    // Order encodes first-event-wins, matching the original classifier: a back who gets to the
    // flat is a flat release for the rest of the play even if he later settles.
    rbFlatMine: ({ key, params }) => rbWentToFlat(key, params) && rbOnMySide(key, params),
    rbCheckdownMine: ({ key, params, latches }) =>
      !latches.rbFlatMine && rbSettledCentral(key, params) && rbOnMySide(key, params),
    /** The back left, this defender cannot take him, and #2 is there to be taken instead. */
    pushCall: (ctx) =>
      !willManRb(ctx) &&
      (rbWentToFlat(ctx.key, ctx.params) || rbSettledCentral(ctx.key, ctx.params)) &&
      canPushToKey2(ctx),
    /** He released somewhere, but not in a way that makes him this defender's man. */
    rbNotMine: ({ key, params, latches }) =>
      !latches.rbCheckdownMine &&
      (rbWentToFlat(key, params) || rbSettledCentral(key, params)) &&
      !(rbWentToFlat(key, params) && rbOnMySide(key, params) && !!params.manAnyReleaseToSide),
  },

  bindings: {
    /**
     * The crosser he picked up, remembered so he keeps trailing that specific receiver. Only
     * hunts for one when the back is not his to man — unless `crossersBeforeRb`, where a crosser
     * outranks the back and he scans from the snap.
     */
    /** #2, taken on a "Push" call. Outranks hunting for a crosser. */
    pushed: (ctx) =>
      ctx.latches.pushCall && ctx.params.pushKey2Slot !== undefined ? ctx.params.pushKey2Slot : null,
    crosser: (ctx) => {
      if (ctx.bindings.pushed !== null) return null; // already have #2
      if (!ctx.params.crossersBeforeRb && willManRb(ctx)) return null;
      for (let c = 0; c < ctx.partners.length; c++) {
        if (isCrosserInMyZone(ctx.partners[c], ctx.params)) return c;
      }
      return null;
    },
  },

  transitions: [
    {
      note: 'Push call — the back is gone and out of reach, so take #2',
      from: ['READ', 'ZONE'],
      to: 'MAN',
      when: ({ bindings }) => bindings.pushed !== null,
    },
    {
      note: 'matched a crosser into the hook',
      from: ['READ', 'ZONE', 'MAN'],
      to: 'MAN',
      when: ({ bindings, state }) => bindings.crosser !== null && state !== 'MAN',
    },
    {
      note: 'the back checked down centrally on my side — take him',
      from: ['READ'],
      to: 'MAN',
      when: (ctx) => willManRb(ctx),
    },
    {
      note: 'the back is not mine (flat, wrong side, or stayed in) — hold the hook',
      from: ['READ'],
      to: 'ZONE',
      when: ({ latches }) => latches.rbNotMine,
    },
  ],

  behavior: {
    // Patient slow drop into the hook, eyes up for crossers.
    READ: ({ params }) => ({ target: params.hookLandmark, speed: params.readSpeed }),

    // Hold the hook — explicitly do NOT chase the back to the flat.
    ZONE: ({ params }) => ({ target: params.hookLandmark, speed: params.zoneDropSpeed }),

    // A locked crosser takes priority over the back: crossers only bind when the back is not
    // being manned, or when `crossersBeforeRb` says they outrank him.
    MAN: (ctx) => {
      // #2 on a push outranks a crosser, which outranks the back.
      const pushed = ctx.bound('pushed');
      if (pushed) return trail(pushed, ctx.params, PURSUIT_LEAD_SECONDS);
      const crosser = ctx.bound('crosser');
      // Lead a crosser (he is running across the defender's face at full speed); trail the back
      // directly, since a checkdown settles rather than running away.
      return crosser
        ? trail(crosser, ctx.params, PURSUIT_LEAD_SECONDS)
        : trail(ctx.key, ctx.params);
    },
  },

  // Never given a reason to leave — a linebacker who saw nothing simply held his zone.
  finalize: (state) => (state === 'READ' ? 'ZONE' : state),
};
